import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import LabeledValue from '../../../../components/common/LabeledValue'
import Modal from '../../../../components/common/Modal/Modal'
import { TimeOffRequestDetails, isVacationBid } from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import {
  TABLE_COLUMNS,
  isVacBidDayInOtherRequestsChecked,
  normalizeStatus,
} from './constants'
import RightPanel from './RightPanel'
import CurrentRequestSection from './CurrentRequestSection'
import HistorySection from './HistorySection'
import WithdrawnView from './WithdrawnView'
import useWithdrawRequest from '../../../../hooks/useWithdrawRequest'
import { RootState } from '../../../../store/store'

interface ResubmittedVacationBidViewProps {
  data: TimeOffRequestDetails
  tableRows: any[]
  highlightedDates: string[]
  handlePrint?: () => void
}

/**
 * Component for displaying resubmitted vacation bid request details
 * Matches the structure of ResubmittedTimeOffView: left panel (details) + right panel (employee info)
 * Includes hold details, resubmission info, history, and current request table/calendar
 */
const ResubmittedVacationBidView: React.FC<ResubmittedVacationBidViewProps> = ({
  data,
  tableRows,
  highlightedDates,
  handlePrint,
}) => {
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false)
  const [withdrawn, setWithdrawn] = useState(false)
  const { withdraw, loading: withdrawLoading, error: withdrawError } = useWithdrawRequest()

  const otherReqData = useSelector(
    (state: RootState) => state.otherReqDetails?.data
  )

  const vacationBid = isVacationBid(data)
  const ns = normalizeStatus(data.status)
  const isReleased = ns === 'resubmitted_released'
  const heading =
    ns === 'resubmitted_onhold'
      ? 'Resubmitted Vacation Bid Request On Hold'
      : 'Resubmitted Vacation Bid Request'

  /**
   * Vacation Bid's "Day in Other Requests" column renders as a checkbox
   * (matching legacy), not the numeric/breakdown value used for regular
   * time-off requests — same treatment as DraftView's/SubmittedOrOnHoldView's
   * Current Request table. Driven by isVacBidDayInOtherRequestsChecked
   * (self-exclusion + active-status check against otherReqData), not the
   * numeric hoursInOtherRequests breakdown — that value is computed from a
   * different, unrelated data shape and does not reflect real overlaps here.
   */
  const currentRequestTableColumns = vacationBid
    ? TABLE_COLUMNS.map((col) =>
        col.key === 'hoursInOtherRequests'
          ? { ...col, label: 'Day in Other Requests' }
          : col
      )
    : TABLE_COLUMNS

  const renderVacationBidOverlapCell = vacationBid
    ? (row: any, col: { key: string }) => {
        if (col.key !== 'hoursInOtherRequests') return row[col.key]
        if (row._totalRow || row.id === 'total') return ''
        return (
          <input
            type="checkbox"
            className="table-cell-checkbox"
            checked={isVacBidDayInOtherRequestsChecked(otherReqData, row.date, row.toReqDtlId ?? null)}
            disabled
            readOnly
            aria-label="Day in other requests"
          />
        )
      }
    : undefined

  if (withdrawn) {
    return (
      <WithdrawnView
        data={{ ...data, status: 'Withdrawn' }}
        tableRows={tableRows}
        highlightedDates={highlightedDates}
        handlePrint={handlePrint}
        showSuccessBanner
      />
    )
  }

  return (
    <div>
      <h1 className="h1">{heading}</h1>

      {/* Left Panel (Details) + Right Panel (Employee Info) */}
      <div className="flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row">
        {/* Left Panel */}
        <div className="flex-1">
          {/* Hold Details Section */}
          {data.holdPlacedOn && (
            <div className="mb-4">
              <LabeledValue label="Hold Placed On" value={data.holdPlacedOn} />
            </div>
          )}
          {data.holdPlacedBy && (
            <div className="mb-4">
              <LabeledValue
                label={isReleased ? 'Released By' : 'Hold Placed By'}
                value={data.holdPlacedBy}
              />
            </div>
          )}
          {vacationBid && (
            <div className="mb-4">
              <LabeledValue label="Request Rank" value={data.requestRank == null ? 'NA' : String(data.requestRank)} />
            </div>
          )}
          <div className="mb-4">
            <LabeledValue label="Approver Comments" value={data.approverComments || '-'} />
          </div>

          <button type="button"
            className="button -primary -inverted mt-8!"
            onClick={() => setIsWithdrawOpen(true)}
          >
            Withdraw Request
          </button>
        </div>

        {/* Right Panel - Employee Info */}
        <RightPanel data={data} variant="name-id" handlePrint={handlePrint} />
      </div>

      {/* Divider */}
      <div className="mt-6!">
        <hr className="-variation-1" />
      </div>

      {/* History Section */}
      <div className="mt-6!">
  
        {/* Audit History Timeline */}
        {data.history && data.history.length > 0 && <HistorySection history={data.history} />}
      </div>

      {/* Current Request Section - Table & Calendar */}
      <CurrentRequestSection
        tableRows={tableRows}
        tableColumns={currentRequestTableColumns}
        renderCell={renderVacationBidOverlapCell}
        highlightedDates={highlightedDates}
      />

      {/* Withdraw Modal */}
      <Modal
        isOpen={isWithdrawOpen}
        title="Withdraw Request"
        onClose={() => setIsWithdrawOpen(false)}
        submitBtn="Withdraw Request"
        backBtn="Cancel"
        loading={withdrawLoading}
        onPrimary={async () => {
          const withdrawSuccess = await withdraw(data.requestId, data.nuid || '')
          if (withdrawSuccess) {
            setIsWithdrawOpen(false)
            setWithdrawn(true)
          }
          return withdrawSuccess
        }}
        onBack={() => setIsWithdrawOpen(false)}
      >
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <img className="w-10 h-10 shrink-0" src="/assets/images/alertSolid.svg" alt="warning" />
          <div>
            <p className="p2 -semibold">
              You are requesting to withdraw this vacation bid request. It will be removed from the
              approvers queue.
            </p>
            {withdrawError && <p className="p3 -medium text-red-600 mt-2">{withdrawError}</p>}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ResubmittedVacationBidView
